// TOOLS ▾ — the student's manual access point to what is NOT the workspace: Big Picture, Structure, Scratch pad, I'm Lost, How TWS Works, each named by its
// PURPOSE. The workspace itself is Essay + Coach. Items stay in the DOM (hidden when closed) so the stable anchors keep working; TWS's own
// invocations (Whole-Orientation Level 2, Structure introductions, I'M LOST suggestions) do not pass through this menu.
import { useEffect, useRef, useState } from "react";
import { GitBranch, Compass, LifeBuoy, BookOpen, ChevronDown, StickyNote } from "lucide-react";
import { useOnboarding } from "@/onboarding/context";
import { GuidanceSwitch } from "@/onboarding/GuidanceSwitch";

export const TOOLS = [
  { id: "big-picture", label: "Big Picture", purpose: "See how your essay is coming together and what needs work.", Icon: Compass },
  { id: "structure", label: "Structure", purpose: "See and reorganize how your ideas fit together.", Icon: GitBranch },
  { id: "scratch", label: "Scratch pad", purpose: "Jot down ideas and play with wording without putting them in your essay.", Icon: StickyNote },
  { id: "lost", label: "I'm Lost", purpose: "Get help figuring out what to do when you're stuck.", Icon: LifeBuoy },
  { id: "guide", label: "How TWS Works", purpose: "See a quick guide to using the Writing Studio.", Icon: BookOpen },
];

export function ToolsMenu({ onBigPicture, onStructure, onScratch, structureActive = false }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const onboarding = useOnboarding();
  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", away); document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", away); document.removeEventListener("keydown", esc); };
  }, [open]);
  const run = {
    "big-picture": () => onBigPicture && onBigPicture(),
    structure: () => onStructure && onStructure(),
    scratch: () => onScratch && onScratch(),
    lost: () => { const b = document.querySelector('[data-testid="orientation-lost"]'); if (b) b.click(); },      // the existing I'M LOST function
    guide: () => onboarding && onboarding.replay(),                                                                 // the existing manual guide
  };
  const testid = { "big-picture": "tools-big-picture", structure: "view-structure", scratch: "tools-scratch", lost: "tools-lost", guide: "tools-guide" };
  return (
    <div ref={ref} data-testid="tools-menu" data-tour="tools-menu" className="relative">
      <button type="button" data-testid="tools-menu-button" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1 rounded-full border border-stone-200 bg-white px-2.5 py-1 text-[11px] font-medium text-stone-600 hover:text-[#8C3A2A] hover:border-stone-300 transition-colors">
        Tools <ChevronDown className={`h-3 w-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <div role="menu" data-testid="tools-menu-list" aria-label="Tools" hidden={!open}
        className={`${open ? "" : "hidden "}absolute right-0 z-40 mt-1.5 w-[min(300px,calc(100vw-2rem))] rounded-xl border border-stone-200 bg-white p-1.5 shadow-lg`}>
        {TOOLS.map(({ id, label, purpose, Icon }) => (
          <button key={id} type="button" role="menuitem" data-testid={testid[id]} data-tour={id === "structure" ? "view-structure" : `tools-${id}`}
            aria-current={id === "structure" && structureActive ? "true" : undefined}
            onClick={() => { setOpen(false); run[id](); }}
            className="flex w-full items-start gap-2.5 rounded-lg px-2.5 py-2 text-left hover:bg-stone-50">
            <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#8C3A2A]" />
            <span className="min-w-0"><span className="block text-[12.5px] font-medium text-stone-800">{label}</span><span className="block text-[11.5px] leading-snug text-stone-500">{purpose}</span></span>
          </button>
        ))}
        {onboarding ? <div className="mt-1 border-t border-stone-100 px-2.5 pt-1.5 pb-1"><GuidanceSwitch /></div> : null}
      </div>
    </div>
  );
}
